import {
  DocumentsFragment_DocumentListWithMissing_Fragment,
  DocumentsFragmentFragment,
  ExceptionsFragmentFragment,
  GetCarriersSearchQuery,
  GetCostCentersQuery,
  GetCustomerLoadsWithRyderNumberQuery,
  GetFuzzyMatchFacilitiesFilterQuery,
  GetFuzzyMatchLocationsFilterQuery,
  GetFuzzyMatchRoutesFilterQuery,
  GetTrailersSearchQuery,
  GetTractorsSearchQuery,
} from '~/src/components/shared/features/__generated_types__/queries.gql'

import {
  Address,
  DocumentResponse,
  FreightRecordReference,
  GpsPoint,
  LoadSource,
  LoadStatus,
  SortInput,
  TrailerType,
  TransitMode,
  Leg as GlobalLeg,
  Stop as GlobalStop,
} from '~/__generated_types__/globalTypes'
import { GetIntegratedLoadDetailQuery } from '~/src/components/RyderShare/TrackingPage/LoadDetailView/__generated_types__/queries.gql'
import { GetIntegratedLoadsQuery } from '~/src/components/RyderShare/TrackingPage/__generated_types__/queries.gql'

export type ListViewColumnStyle = {
  name: string
  widthPct?: number
  className?: string
}

export type SortingProps = {
  name: string
  direction: SortInput
}

export type LocationFields = {
  address?: Address | null
  name?: string | null
  id: bigint
  timeZoneCode: string
  timeZoneName: string
}

export type UnionStopEstimate = {
  estimatedArrivalTime?: string | null | undefined
  estimatedDepartureTime?: string | null | undefined
  estimatedDwellTime?: number | null | undefined
  milesFromPrevStop?: number | null | undefined
  estimatedMilesFromPrevStop?: number | null | undefined
  timeFromPrevStop?: number | null | undefined
  estimatedTimeFromPrevStop?: number | null | undefined
}

export type UnionStop = {
  appointmentEnd?: string | null
  appointmentStart?: string | null
  customerAppointmentEnd?: string | null
  isAppointmentValid?: boolean
  arrivalTime?: string | null
  departureTime?: string | null
  documents: DocumentResponse
  estimateData?: UnionStopEstimate | null
  globalIdx?: number
  id: bigint
  idx: number
  location?: LocationFields | null
}

export type GeoRoutes = Array<{
  expectedRoute: Array<GpsPoint>
  actualRoute: Array<GpsPoint>
}>

export type ColoredStopMarker = {
  color: {
    twColor: string
    hexColor: string
  }
  stop: UnionStop
}

export type GPSPoint = { lat: number; lng: number }

export type Polygon = {
  id?: number
  bounds: Array<GPSPoint>
}

export type GeoAddress = {
  line1: string
  line2?: string | null
  city: string
  postalCode: string
  countryCode: string
  stateOrProvinceAbbrev: string
}

export type Location = {
  id: bigint
  name: string
  note?: string
  gpsPoint: GPSPoint
  radius: number
  gpsPolygons: Array<Polygon>
  address?: GeoAddress
}

export type GeoRoute = {
  expectedRoute: Array<GpsPoint>
  actualRoute: Array<GpsPoint>
}

export type Route = GetFuzzyMatchRoutesFilterQuery['getRoutes']['results'][0]
export type Trailer = GetTrailersSearchQuery['getTrailers']['results'][number]
export type Tractor = GetTractorsSearchQuery['getTractors']['results'][number]

export type TrailerSearchResultSource = 'equipment' | 'reference'

export type TrailerSearchResult = {
  id: string
  unitNumber: string
  source: TrailerSearchResultSource
  type?: TrailerType
}

export type StopLocation =
  GetFuzzyMatchLocationsFilterQuery['getLocations']['results'][number]
export type CostCenter = GetCostCentersQuery['getCostCenters']['results'][number]
export type Facility =
  GetFuzzyMatchFacilitiesFilterQuery['getFacilities']['results'][number]
export type Carrier = GetCarriersSearchQuery['getCarriers'][number]

export type Supplier = {
  id: string
  value: string
}

export enum UserType {
  CUSTOMER = 'customer',
  OPERATOR = 'operator',
}

export enum FreightStatus {
  PLANNED = 'Planned',
  PICKED_UP = 'Picked up',
  DELIVERED = 'Delivered',
  OVER = 'Over',
  SHORT = 'Short',
  DAMAGED = 'Damaged',
  CANCELLED = 'Cancelled',
}

// TODO: Update All places that use StopStatus to use this shared enum
export enum StopStatus {
  Completed = 'CMP',
  AtLocation = 'LOC',
  InTransit = 'TRN',
  Planned = 'PLN',
  Cancelled = 'CAN',
  Skipped = 'SKP',
  Attempted = 'ATM',
}

export type Document = Extract<
  DocumentsFragment_DocumentListWithMissing_Fragment['documents'][0],
  { __typename: 'Document' }
>

export type MissingDocument = Extract<
  DocumentsFragment_DocumentListWithMissing_Fragment['documents'][0],
  { __typename: 'MissingDocument' }
>

export type MissingDocumentWithDocInfo = MissingDocument & { docInfo?: string | null }

export type ExceptionTypes = ExceptionsFragmentFragment

export type AttemptedStopException = Extract<
  ExceptionTypes,
  { __typename: 'AttemptedStopException' }
>

export type CustomerLateDeliveryException = Extract<
  ExceptionTypes,
  { __typename: 'CustomerLateDeliveryException' }
>

export type CustomerLatePickupException = Extract<
  ExceptionTypes,
  { __typename: 'CustomerLatePickupException' }
>

export type DamageException = Extract<ExceptionTypes, { __typename: 'DamageException' }>

export type LateArrivalException = Extract<
  ExceptionTypes,
  { __typename: 'LateArrivalException' }
>

export type MissingDocumentException = Extract<
  ExceptionTypes,
  { __typename: 'MissingDocumentException' }
>

export type OverageException = Extract<
  ExceptionTypes,
  { __typename: 'OverageException' }
>

export type ShortageException = Extract<
  ExceptionTypes,
  { __typename: 'ShortageException' }
>

export type SkippedStopException = Extract<
  ExceptionTypes,
  { __typename: 'SkippedStopException' }
>

export type LoadWithAllFields = GetIntegratedLoadsQuery['getLoads']['results'][number]

type LoadStop =
  GetIntegratedLoadsQuery['getLoads']['results'][number]['legs'][number]['stops'][number]
export type ListStop = Omit<
  LoadStop,
  | 'stopReferences'
  | 'arrivalTime'
  | 'departureTime'
  | 'appointmentStart'
  | 'customerAppointmentEnd'
> & {
  stopReferences: Array<{
    __typename?: 'StopReference'
    id: number
    value: string
    stopId: bigint
    type: string
  }>
  arrivalTime?: string | null
  departureTime?: string | null
  customerAppointmentEnd?: string | null
  appointmentStart?: string | null
}
type LoadLeg = GetIntegratedLoadsQuery['getLoads']['results'][number]['legs'][number]
export type Leg = Omit<LoadLeg, 'plannedEndTime' | 'stops'> & {
  plannedEndTime?: string | null
  stops: Array<ListStop>
}
export type Load = Omit<LoadWithAllFields, 'legs'> & {
  legs: Array<Leg>
}

export type DetailedLoad = {
  endTime?: string | null
  id: number
  ryderNumber?: string | null
  startTime?: string | null
  status: LoadStatus
  source: LoadSource
  legs: Array<LegWithEstimates>
  transitMode?: TransitMode
  documents: DocumentsFragmentFragment
  freightDetails?: {
    freightRecords: Array<{
      stopId: bigint
      references: Array<FreightRecordReference>
    }>
  }
  trailer?: {
    unitNumber?: string | null
  } | null
  costCenter?: {
    __typename?: 'CostCenter'
    name: string
    id: number
    code?: string | null
  } | null
  route?: { __typename?: 'Route'; id: number; code: string; name: string } | null
  customerReferenceNumbers?: Array<{
    __typename?: 'CustomerReferenceNumber'
    id: number
    type: string
    value: string
  }>
  searchAndDisplayReferences?: Array<{
    __typename?: 'CustomerReferenceNumber'
    id: number
    type: string
    value: string
  }>
}

export type LegWithEstimates = Omit<
  GetIntegratedLoadDetailQuery['getLoad']['legs'][number],
  'stops'
> & {
  stops: Array<Stop>
}
export type Stop =
  GetIntegratedLoadDetailQuery['getLoad']['legs'][number]['stops'][number] & {
    globalIdx?: number
  }
export type CustomerReferenceNumber =
  GetIntegratedLoadDetailQuery['getLoad']['customerReferenceNumbers'][number]
export type Reference = GetCustomerLoadsWithRyderNumberQuery['getLoads']['results'][0]
export type ReferenceNumber = Omit<
  GetIntegratedLoadDetailQuery['getLoad']['customerReferenceNumbers'][number],
  '__typename'
> & { stopId?: bigint; freightRecordId?: number; displayName?: string | null }

// Aliases for backward compatibility
export type CustomerDetailedLoad = DetailedLoad
export type CustomerLeg = LegWithEstimates
export type CustomerStop = Stop
export type CVTReferenceNumber = ReferenceNumber

export type StopForLatenessViolations = Pick<
  GlobalStop,
  'idx' | 'status' | 'latenessStatus' | 'appointmentEnd' | 'arrivalTime'
>

export type LegForLatenessViolations = Pick<GlobalLeg, 'idx' | 'status'> & {
  stops: Array<StopForLatenessViolations>
}

export type TrailerInfo = {
  id: number
  unitNumber?: string | null
  type?: TrailerType
}
